if (!customElements.get('zone-finder')) {
  customElements.define(
    'zone-finder',
    class ZoneFinder extends HTMLElement {
      // Static property to cache individual zip lookups
      static zipLookupCache = new Map();
      
      // Storage key for localStorage
      static STORAGE_KEY = 'zone-finder-data';
      
      // Cache for individual zip lookups in localStorage
      static ZIP_CACHE_KEY = 'zone-finder-zip-cache';
      
      // API endpoint for GeoIP lookups
      static GEOIP_API_URL = 'https://etgb.netlify.app/api/geoip';
      
      // Cache duration for zip lookups (6 months - hardiness zones are very stable)
      static ZIP_CACHE_DURATION = 6 * 30 * 24 * 60 * 60 * 1000;

      constructor() {
        super();

        // Store references to DOM elements
        this.inputZipCode = this.querySelector('input');
        this.zoneResultsText = this.querySelector('.js-zone-results-text');
        this.zoneResult = this.querySelector('.js-zone-result');
        this.zoneZipcode = this.querySelector('.js-zone-zipcode');
        this.zoneError = this.querySelector('.js-zone-error');
        this.zoneShopButton = this.querySelector('.js-zone-shop');
        this.zoneSeparator = this.querySelector('.zone-finder__option--separator');

        // Define collections mapping
        this.collections = {
          '03': '/collections/planting-zone-3',
          '04': '/collections/planting-zone-4',
          '5A': '/collections/planting-zone-5',
          '5B': '/collections/planting-zone-5',
          '6A': '/collections/planting-zone-6',
          '6B': '/collections/planting-zone-6',
          '7A': '/collections/planting-zone-7',
          '7B': '/collections/planting-zone-7',
          '8A': '/collections/planting-zone-8',
          '8B': '/collections/planting-zone-8',
          '9A': '/collections/planting-zone-9',
          '9B': '/collections/planting-zone-9',
          '10A': '/collections/planting-zone-10',
          '10B': '/collections/planting-zone-10',
          '11A': '/collections/planting-zone-11',
          '11B': '/collections/planting-zone-11',
        };

        // Store unsubscribe function
        this.unsubscribeZoneUpdate = null;

        // Bind event handlers
        this.findZone = this.findZone.bind(this);
        this.handleInputInteraction = this.handleInputInteraction.bind(this);
        this.handleZoneUpdate = this.handleZoneUpdate.bind(this);
      }

      connectedCallback() {
        // Add event listeners
        this.addEventListener('submit', this.findZone);
        this.inputZipCode.addEventListener('focus', this.handleInputInteraction);
        this.inputZipCode.addEventListener('input', this.handleInputInteraction);
        
        // Subscribe to zone update events from other zone finder instances
        this.unsubscribeZoneUpdate = subscribe(PUB_SUB_EVENTS.zoneUpdate, this.handleZoneUpdate);
        
        // Load cached zip lookups from localStorage
        this.loadZipCache();
        
        // Check for stored data and auto-populate, or try auto-location
        const hasStoredData = this.loadStoredZoneData();
        if (!hasStoredData) {
          this.tryAutoLocation();
        }
      }

      disconnectedCallback() {
        // Clean up event listeners
        this.removeEventListener('submit', this.findZone);
        this.inputZipCode.removeEventListener('focus', this.handleInputInteraction);
        this.inputZipCode.removeEventListener('input', this.handleInputInteraction);
        
        // Clean up subscription
        if (this.unsubscribeZoneUpdate) {
          this.unsubscribeZoneUpdate();
          this.unsubscribeZoneUpdate = null;
        }
      }

      handleZoneUpdate(eventData) {
        // Update this instance with new zone data from any source
        // The pub/sub system ensures we get updates from other zone finder instances
        if (eventData && eventData.zipCode && eventData.zoneData && eventData.zoneData.success) {
          // Update the input field
          this.inputZipCode.value = eventData.zipCode;
          
          // Update the zone display
          this.updateZoneDisplay(eventData.zipCode, eventData.zoneData);
        }
      }

      saveZoneData(zipCode, zoneData) {
        try {
          const dataToStore = {
            zipCode: zipCode,
            zoneData: zoneData,
            timestamp: Date.now() // Optional: for future expiration logic
          };
          localStorage.setItem(ZoneFinder.STORAGE_KEY, JSON.stringify(dataToStore));
          
          // Publish zone update event
          publish(PUB_SUB_EVENTS.zoneUpdate, {
            source: 'zone-finder',
            zipCode: zipCode,
            zoneData: zoneData,
          });
        } catch (error) {
          console.warn('Unable to save zone data to localStorage:', error);
        }
      }

      loadStoredZoneData() {
        try {
          const storedData = localStorage.getItem(ZoneFinder.STORAGE_KEY);
          if (storedData) {
            const parsedData = JSON.parse(storedData);
            
            // Validate the stored data structure
            if (parsedData.zipCode && parsedData.zoneData && parsedData.zoneData.success) {
              // Populate the input field
              this.inputZipCode.value = parsedData.zipCode;
              
              // Update the zone display with stored data
              this.updateZoneDisplay(parsedData.zipCode, parsedData.zoneData);
              
              return true; // Data was loaded successfully
            }
          }
        } catch (error) {
          console.warn('Unable to load stored zone data:', error);
          // Clear corrupted data
          this.clearStoredZoneData();
        }
        
        // No valid data was found - reset to empty state
        this.resetToEmptyState();
        return false; // No data was loaded
      }

      clearStoredZoneData() {
        try {
          localStorage.removeItem(ZoneFinder.STORAGE_KEY);
        } catch (error) {
          console.warn('Unable to clear stored zone data:', error);
        }
      }

      // Static method to manually clear zip cache (useful for updates)
      static clearZipCache() {
        try {
          localStorage.removeItem(ZoneFinder.ZIP_CACHE_KEY);
          ZoneFinder.zipLookupCache.clear();
          console.log('Zone finder cache cleared successfully');
        } catch (error) {
          console.warn('Unable to clear zip cache:', error);
        }
      }

      loadZipCache() {
        try {
          const cachedData = localStorage.getItem(ZoneFinder.ZIP_CACHE_KEY);
          if (cachedData) {
            const parsedData = JSON.parse(cachedData);
            // Convert back to Map and filter out old entries
            const now = Date.now();
            // Cache for 6 months - hardiness zones are very stable data
            // This balances performance with allowing for occasional updates
            const maxAge = ZoneFinder.ZIP_CACHE_DURATION;
            
            Object.entries(parsedData).forEach(([zip, data]) => {
              if (now - data.timestamp < maxAge) {
                ZoneFinder.zipLookupCache.set(zip, data);
              }
            });
          }
        } catch (error) {
          console.warn('Unable to load zip cache:', error);
        }
      }

      saveZipCache() {
        try {
          // Convert Map to plain object for storage
          const cacheObject = Object.fromEntries(ZoneFinder.zipLookupCache);
          localStorage.setItem(ZoneFinder.ZIP_CACHE_KEY, JSON.stringify(cacheObject));
        } catch (error) {
          console.warn('Unable to save zip cache:', error);
        }
      }

      async tryAutoLocation() {
        try {
          // Only try auto-location once per session to avoid repeated API calls
          if (sessionStorage.getItem('zone-finder-auto-location-attempted')) {
            return;
          }
          
          sessionStorage.setItem('zone-finder-auto-location-attempted', 'true');
          
          const response = await fetch(ZoneFinder.GEOIP_API_URL);
          if (!response.ok) {
            throw new Error('GeoIP API request failed');
          }
          
          const data = await response.json();
          
          if (data.success && data.zipCode && data.zone) {
            // Cache this lookup
            const cacheData = {
              zone: data.zone,
              city: data.geo?.city,
              timestamp: Date.now()
            };
            ZoneFinder.zipLookupCache.set(data.zipCode, cacheData);
            this.saveZipCache();
            
            // Auto-populate the form
            this.inputZipCode.value = data.zipCode;
            
            // Format the zone data for display
            const zoneData = {
              success: true,
              zone: data.zone,
              city: data.geo?.city,
              collectionURL: this.collections[data.zone]
            };
            
            this.updateZoneDisplay(data.zipCode, zoneData);
            this.saveZoneData(data.zipCode, zoneData);
          }
        } catch (error) {
          console.warn('Auto-location failed:', error);
          // Silently fail - user can still manually enter zip
        }
      }

      async handleInputInteraction() {
        // No longer need to preload data - we'll fetch on demand
      }

      async getZoneForZipCode(zipCode) {
        // Validate zip code length and format
        if (zipCode.length !== 5 || !/^\d{5}$/.test(zipCode)) {
          return {
            success: false,
            error: 'Please enter a valid 5-digit zip code.'
          };
        }

        // Check if zip code exists in cache first
        if (ZoneFinder.zipLookupCache.has(zipCode)) {
          const cachedData = ZoneFinder.zipLookupCache.get(zipCode);
          const outputZone = cachedData.zone;
          const outputCity = cachedData.city;
          const outputCollectionURL = this.collections[cachedData.zone];
          
          return {
            success: true,
            zone: outputZone,
            city: outputCity,
            collectionURL: outputCollectionURL
          };
        }

        // If not in cache, fetch from API
        try {
          const response = await fetch(`${ZoneFinder.GEOIP_API_URL}?zip=${zipCode}`);
          if (!response.ok) {
            throw new Error('API request failed');
          }
          
          const data = await response.json();
          
          if (data.success && data.zone) {
            // Cache this successful lookup
            const cacheData = {
              zone: data.zone,
              city: data.geo?.city,
              timestamp: Date.now()
            };
            ZoneFinder.zipLookupCache.set(zipCode, cacheData);
            this.saveZipCache();
            
            const outputZone = data.zone;
            const outputCity = data.geo?.city;
            const outputCollectionURL = this.collections[data.zone];
            
            return {
              success: true,
              zone: outputZone,
              city: outputCity,
              collectionURL: outputCollectionURL
            };
          } else {
            return {
              success: false,
              error: data.error || 'No zone found for this zip code'
            };
          }
        } catch (error) {
          console.error('Zone lookup failed:', error);
          return {
            success: false,
            error: 'Zone not found. Please try again.'
          };
        }
      }

      updateZoneDisplay(zipCode, zoneData) {
        if (zoneData.success) {
          // Success state - show results
          this.zoneResultsText.textContent = 'Plant hardiness zone for';
          this.zoneZipcode.textContent = zipCode + ':';
          this.zoneResult.textContent = zoneData.zone;
          this.zoneError.textContent = ''; // Clear any error
          
          if (zoneData.collectionURL) {
            // Valid zone with available products - show separator and button
            this.zoneShopButton.textContent = `Shop Planting Zone ${zoneData.zone}`;
            this.zoneShopButton.href = zoneData.collectionURL;
            this.zoneShopButton.removeAttribute('aria-disabled');
            this.zoneShopButton.removeAttribute('disabled');
            
            // Show the separator and button
            this.zoneSeparator.classList.remove('hidden');
            this.zoneShopButton.classList.remove('hidden');
          } else {
            // Valid zone but no products available - hide separator and button
            this.zoneSeparator.classList.add('hidden');
            this.zoneShopButton.classList.add('hidden');
          }
        }
      }

      showError(errorMessage) {
        // Show error without affecting existing zone display
        this.zoneError.textContent = errorMessage;
      }

      resetToEmptyState() {
        // Empty state - no zone data available
        this.zoneResultsText.textContent = 'Find your hardiness zone';
        this.zoneZipcode.textContent = '';
        this.zoneResult.textContent = '';
        this.zoneError.textContent = '';
        
        // Hide separator and button in empty state
        this.zoneSeparator.classList.add('hidden');
        this.zoneShopButton.classList.add('hidden');
      }

      async findZone(event) {
        event.preventDefault(); // Prevent default form submission

        const zipCodeInput = this.querySelector('input').value.trim(); // Trim whitespace
        const zipCode = zipCodeInput; // Treat zip code as a string to preserve leading zeros

        // Show loading state
        this.setAttribute('loading', '');
        this.showError(''); // Clear any existing errors

        try {
          // Get zone data for the zip code (now async)
          const zoneData = await this.getZoneForZipCode(zipCode);
          
          if (zoneData.success) {
            // Update the DOM with successful results
            this.updateZoneDisplay(zipCode, zoneData);
            
            // Save successful results to localStorage
            this.saveZoneData(zipCode, zoneData);
          } else {
            // Show error without affecting existing zone display
            this.showError(zoneData.error);
          }
        } catch (error) {
          console.error('Zone lookup failed:', error);
          this.showError('Zone not found. Please try again.');
        } finally {
          // Always remove loading state
          this.removeAttribute('loading');
        }
      }
    }
  );
}